import { useState, useCallback, useEffect, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useNodesInitialized,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { InboxWorkflowNode } from "./InboxWorkflowNode";

const nodeTypes = { inboxWorkflowNode: InboxWorkflowNode };

const NODE_GAP = 40;

function FlowCanvas({ initialNodes, edges }) {
  const [nodes, setNodes] = useState(initialNodes);
  const nodesInitialized = useNodesInitialized();
  const hasRepositioned = useRef(false);
  const { fitView } = useReactFlow();

  // Sync nodes when props change
  useEffect(() => {
    setNodes(initialNodes);
    hasRepositioned.current = false;
  }, [initialNodes]);

  // Handle dimension/position changes from ReactFlow
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updated = [...nds];
      for (const change of changes) {
        if (change.type === "dimensions" && change.dimensions) {
          const idx = updated.findIndex((n) => n.id === change.id);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], measured: change.dimensions };
          }
        } else if (change.type === "position" && change.position) {
          const idx = updated.findIndex((n) => n.id === change.id);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], position: change.position };
          }
        }
      }
      return updated;
    });
  }, []);

  // Reposition nodes after measurement
  useEffect(() => {
    if (!nodesInitialized || hasRepositioned.current) return;
    if (nodes.length <= 1) return;

    const allMeasured = nodes.every((n) => n.measured?.height);
    if (!allMeasured) return;

    const sorted = [...nodes].sort((a, b) => a.position.y - b.position.y);
    let yOffset = sorted[0].position.y;
    let changed = false;

    const repositioned = sorted.map((node) => {
      if (Math.abs(node.position.y - yOffset) > 1) changed = true;
      const newNode = { ...node, position: { ...node.position, y: yOffset } };
      const measuredHeight = node.measured?.height || 80;
      yOffset += measuredHeight + NODE_GAP;
      return newNode;
    });

    if (!changed) {
      hasRepositioned.current = true;
      return;
    }

    hasRepositioned.current = true;
    setNodes(repositioned);
    setTimeout(() => fitView({ padding: 0.3 }), 50);
  }, [nodesInitialized, nodes, fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      minZoom={0.3}
      maxZoom={1.5}
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
    >
      <Background color="#cbd5e1" gap={24} size={1} />
      <Controls
        className="bg-white shadow-sm border border-slate-200 rounded-lg overflow-hidden"
        showInteractive={false}
      />
    </ReactFlow>
  );
}

export function InboxWorkflowPanel({ nodes, edges, workflowTitle, emailId }) {
  if (!nodes || nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full rounded-xl border border-slate-200 bg-gray-50/50">
        <div className="text-center">
          <EnvelopeIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400 font-medium">
            Select an email to view its workflow
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-900">
          {workflowTitle}
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">
          {nodes.length} step{nodes.length !== 1 ? "s" : ""} in this workflow
        </p>
      </div>
      {/* Flow area */}
      <div className="flex-1" style={{ backgroundColor: "#fafafa" }}>
        <ReactFlowProvider key={emailId || "empty"}>
          <FlowCanvas initialNodes={nodes} edges={edges} />
        </ReactFlowProvider>
      </div>
    </div>
  );
}
